<template>
  <li class="relative" :class="[enlargedMode ? enlargedContainerClass : tileContainerClass]">
    <!-- Stream Device Icon -->
    <div
      class="
        bg-vice-teal-600
        rounded-full
        w-10
        h-10
        content-center
        justify-center
        absolute
        top-2
        left-2
        tooltip
      "
      :class="sessionStore.isPhasePaused ? 'z-20' : 'z-30'"
    >
      <ViceScreenshare class="h-7 w-7 m-auto" />
      <span class="tooltip-text ml-2">Screenshare</span>
    </div>
    <!-- Mute -->
    <ToggleMuteStream
      v-if="isLiveConsultation"
      class="z-30"
      :isStreamMuted="streamMuted"
      @stream-state-toggled="toggleStreamState"
    />

    <div class="group block h-full overflow-hidden relative rounded-2xl z-20 bg-black">
      <!-- Icon and trigger -->
      <div class="absolute top-0 right-0 z-10" v-if="isLiveConsultation">
        <div class="p-3 relative cursor-pointer" :class="{ 'sm:p-1': !enlargedMode }">
          <div class="relative flex items-center gap-2">
            <div
              data-cy="toggle-focus"
              :class="{ 'sm:p-1': !enlargedMode }"
              class="
                bg-vice-dark-grey bg-opacity-70
                text-white
                rounded-full
                flex
                justify-center
                items-center
                p-2
                hover:bg-opacity-90
              "
            >
              <ArrowsExpandIcon
                class="h-6 w-6"
                v-if="!enlargedMode"
                :class="{ 'sm:w-5 sm:h-5': !enlargedMode }"
                @click="onEnlargeIconClick(tileId)"
              />
              <ArrowsCollapseIcon class="p-0.5" @click="onMinimizeIconClick" v-else />
            </div>
          </div>
        </div>
      </div>

      <!-- Screenshare preview -->
      <StreamPlayerRemote
        :streamMuted="streamMuted"
        :jitsiUserId="userJitsiId"
        :setVideoAudioRefs="setVideoAudioRefs"
        :showVideo="shouldShowRemoteUserVideo(userJitsiId)"
        :isAudioMuted="sessionStore.isPutOnHold || sessionStore.isPhasePaused"
      />

      <span v-if="streamMuted" class="muted-text w-full items-center justify-center rounded-xl z-5">
        PAUSED
      </span>

      <UserLabel displayName="Screenshare" :hideStats="true" :isExternalDevice="true" />
    </div>
  </li>
</template>

<script>
import { computed, onUnmounted, ref } from 'vue';
import { ArrowsExpandIcon } from '@heroicons/vue/outline';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import ViceScreenshare from '@/Components/Icons/ViceScreenshare.vue';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import ArrowsCollapseIcon from '@/Components/Icons/ArrowsCollapseIcon.vue';
import ToggleMuteStream from '@/Components/Inputs/ToggleMuteStream.vue';
import { VICE_SESSION_PHASES } from '@/Utils/vice-constants';

export default {
  computed: {
    VICE_SESSION_PHASES() {
      return VICE_SESSION_PHASES;
    },
  },
  props: {
    index: {
      type: [Number, String],
      required: true,
    },
    userJitsiId: {
      type: [Number, String],
    },
    remoteDisplayInfo: {
      type: Object,
      required: true,
    },
    shouldShowRemoteUserVideo: {
      type: Function,
      required: true,
    },
    setVideoAudioRefs: {
      type: Function,
      required: true,
    },
    tileId: {
      type: String,
      default: 'enlargeableScreenshare',
    },
    gridDimension: {
      type: Object,
      required: true,
    },
    enlargedJitsiId: {
      type: String,
    },
    muteMedia: {
      type: Function,
    },
  },
  emits: ['toggleEnlargeMode'],
  components: {
    ToggleMuteStream,
    ArrowsExpandIcon,
    ArrowsCollapseIcon,
    UserLabel,
    ViceScreenshare,
    StreamPlayerRemote,
  },
  setup(props, { emit }) {
    const streamMuted = ref(false);
    const sessionStore = useSessionStore();
    const tileContainerClass = computed(
      () =>
        `col-start-${props.gridDimension[props.index].start ?? 1} col-span-${
          props.gridDimension[props.index].cols
        } row-span-${props.gridDimension[props.index].rows}`,
    );
    const enlargedContainerClass = 'col-start-2 col-span-10 row-span-8 row-start-1';
    const enlargedMode = computed(
      () => props.enlargedJitsiId === props.userJitsiId && props.userJitsiId !== null,
    );

    const isLiveConsultation = computed(
      () =>
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION && sessionStore.hasLivePhase,
    );

    const onEnlargeIconClick = () => {
      emit('toggleEnlargeMode', props.userJitsiId);
    };

    const onMinimizeIconClick = () => {
      emit('toggleEnlargeMode');
    };

    const toggleStreamState = (e) => {
      streamMuted.value = e;
      props.muteMedia(props.userJitsiId, e);
    };

    onUnmounted(async () => {
      if (enlargedMode.value) onMinimizeIconClick();
    });

    return {
      streamMuted,
      sessionStore,
      enlargedMode,
      tileContainerClass,
      isLiveConsultation,
      enlargedContainerClass,
      toggleStreamState,
      onEnlargeIconClick,
      onMinimizeIconClick,
    };
  },
};
</script>
<style scoped>
video {
  transform: scale(-1, 1); /*For Firefox (& IE) */
  -webkit-transform: scale(-1, 1); /*for Chrome & Opera (& Safari) */
}

.text-shadow {
  text-shadow: 0 0 2px black;
}

.shadow-filter {
  filter: drop-shadow(0 0 2px black);
}

.hide {
  display: none;
}

.row-span-3 {
  grid-row-start: span 3;
  grid-row-end: span 3;
}

.col-span-3 {
  grid-column-start: span 3;
  grid-column-end: span 3;
}

.row-span-4 {
  grid-row-start: span 4;
  grid-row-end: span 4;
}

.row-span-5 {
  grid-row-start: span 5;
  grid-row-end: span 5;
}
</style>
